/**
 * @file Fixed Signify mapping values — the part of the integration that is NOT
 * per-org configuration, and nothing here is overridable. Everything an admin
 * can change lives in the `parameters/signifyMappings` document typed as
 * `SignifyMappingsConfig` (`SignifyApi.types.ts`), including every per-brand
 * price preference: a brand preference is a field rule with a condition, so it
 * is configuration and SYD-2425 supplies the values. What remains here is
 * structural: identifiers the upsert logic depends on, and configuration-session
 * paths no UI exposes. Consumed by the React Signify hooks and by the Functions
 * workflow seeds. Adding a mapping here rather than to the config doc removes an
 * admin's ability to change it — that is the whole distinction the split encodes.
 */

/**
 * Identifies Signify as the data source on every record the integration writes.
 * Distinguishes Signify products from other manufacturer-API products during
 * the `manufacturersSourceId` + `manufacturersProductId` duplicate lookup.
 */
export const REQUIRED_SOURCE_ID_MAPPING = 'SignifyAPI';

/**
 * Identifies a unique product for upsert. Not configurable: `product.code` must
 * be present in the API response or the record cannot be created at all, and
 * changing the destination would orphan every previously imported record from
 * its duplicate check.
 *
 * `manufacturersSourceId` needs no entry — it is set to
 * `REQUIRED_SOURCE_ID_MAPPING` directly rather than read from the response.
 */
export const REQUIRED_PRODUCT_ID_MAPPING = {
  'product.code': 'manufacturersProductId'
} as const;

/**
 * The reserved `fieldMappings` destination that names the created record. The
 * rules under it are ordinary configuration and carry brand conditions like any
 * other destination, and the line-item builder pulls this destination out of
 * the resolved set instead of writing it as a field.
 *
 * The key sits in the Products field-id namespace, so a Products field whose
 * short label is "Record Name" generates exactly this id and would share the
 * one `fieldMappings` entry — its rules would become the record's name. What
 * prevents that is `useSignifyProductFields`, which drops the id from the
 * fields the mapping UIs offer, at the cost of making a real field with that id
 * unreachable from Signify. The four `SIGNIFY_CONFIGURATION_FIELD_MAPPINGS`
 * destinations share the namespace unfiltered, and a Products field carrying
 * one of those ids is replaced at build time because the fixed maps spread
 * last.
 */
export const SIGNIFY_RECORD_NAME_DESTINATION = 'recordName';

/**
 * Signify configuration-session (punchout) paths -> lineItem fieldIds. Fixed
 * rather than configurable because these fields carry the session identity the
 * configurator round-trip depends on; the mapping UIs render them read-only.
 */
export const SIGNIFY_CONFIGURATION_FIELD_MAPPINGS: Record<string, string> = {
  'configuration.sessionId': 'configurationId',
  'configuration.commercialCode': 'commercialCode',
  'configuration.complete': 'configurationComplete',
  'configuration.variableAssignments': 'configurationVariableAssignments'
};
